import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { Printer, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ReturnedReception, ReturnedReceptionStatus } from '../../models/Reused';

export interface ReusedCardProps extends RenderItemProps<ReturnedReception> {
  className?: string;
  onPrint?: (item: ReturnedReception) => void;
  onView?: (item: ReturnedReception) => void;
}

export const ReusedCard = ({ item: reception, className = '', onPrint, onView }: ReusedCardProps) => {
  const { t } = useTranslation('reusedModule');

  const getStatusBadge = (status: ReturnedReceptionStatus) => {
    switch (status) {
      case ReturnedReceptionStatus.OPEN:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-card-border text-title">
            {t('status.open', 'Abierto')}
          </span>
        );
      case ReturnedReceptionStatus.POR_UBICAR:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-bg-warning text-warning border border-warning/20">
            {t('status.to_locate', 'Por ubicar')}
          </span>
        );
      case ReturnedReceptionStatus.CLOSED:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-bg-success text-success border border-success/20">
            {t('status.closed', 'Cerrado')}
          </span>
        );
      case ReturnedReceptionStatus.PENDING_OPEN:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-card-border light:bg-bg-warning text-warning border border-warning/20">
            {t('status.pending_open', 'Pendiente')}
          </span>
        );
      default:
        return null;
    }
  };

  const formattedDate = new Date(reception.created_at.replace(' ', 'T')).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <div className={`p-4 border border-card-border rounded-lg bg-card-bg mb-3 shadow-sm ${className}`}>
      <div className="flex flex-col gap-3">
        {/* Primera Fila: Referencia y Estado */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-title truncate max-w-[60%]">{reception.code}</span>
          {getStatusBadge(reception.status)}
        </div>

        {/* Segunda Fila: Metadatos */}
        <div className="flex items-center justify-between text-xs text-subtext">
          <div className="flex items-center gap-2">
            <span>{formattedDate}</span>
            <span>•</span>
            <span>
              {reception.orders_count || 0} {t('card.orders', 'pedidos')}
            </span>
          </div>
        </div>

        {/* Tercera Fila: Acciones */}
        <div className="flex items-center gap-2 pt-1">
          <Button
            variant={reception.status === ReturnedReceptionStatus.OPEN ? 'primary' : 'outline'}
            size="sm"
            className="flex-1 h-9"
            onClick={() => onView?.(reception)}
          >
            {reception.status === ReturnedReceptionStatus.OPEN ? (
              t('actions.receive', 'Recepcionar')
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" />
                {t('actions.view', 'Ver')}
              </>
            )}
          </Button>

          {(reception.status === ReturnedReceptionStatus.POR_UBICAR ||
            reception.status === ReturnedReceptionStatus.CLOSED ||
            reception.status === ReturnedReceptionStatus.OPEN) && (
            <Button
              variant="outline"
              size="sm"
              className="w-12 h-9 p-0 border-card-border"
              onClick={() => onPrint?.(reception)}
            >
              <Printer className="w-4 h-4 text-icons" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
